/**
 * NoteIndicatorBadge — a clickable listing-row chip that signals presence of
 * notes and navigates the user into the operating context where they can
 * read/post them.
 *
 * Replaces v1's stateless `NoteIndicator` (a dead-end count badge). Per the
 * CR-SEQ-2026-013 v3 plan: indicators earn their visual weight only where
 * presence-of-context changes the user's next action — and a chip that goes
 * nowhere is the wrong UX. The parent listing supplies the click target
 * (`onClick`); destinations vary per listing (see the plan's table).
 *
 * Per CR §1.2.5 + §1.6 + FRD §6: the badge never renders any portion of the
 * note body. Content is only reachable from the artifact's primary surface or
 * a consolidated audit trail.
 *
 * Empty-state handling (Phase 3.x — universal Notes affordance): when the row
 * has zero notes AND the viewer can create notes, the badge renders a quieter
 * "compose" ghost so there's always an entry point for the first note. This
 * closes the gap where entities created without a wizard-NoteStep had no way
 * to receive a follow-up note. For viewers without CreateNotes, the badge
 * stays hidden at count=0 (no actionable target).
 */
import {
  ClipboardDocumentListIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { useAppContext } from "@context/AppContext";

type NoteIndicatorBadgeProps = {
  count: number | undefined;
  /** Concrete destination wired per listing. See plan's destinations table. */
  onClick: () => void;
  /**
   * When `false`, the badge is non-interactive and only shows the count.
   * Defaults to `true`. Currently unused but defined for forward-compat.
   */
  interactive?: boolean;
  className?: string;
};

const NoteIndicatorBadge: React.FunctionComponent<NoteIndicatorBadgeProps> = ({
  count,
  onClick,
  interactive = true,
  className,
}) => {
  const { t } = useTranslation();
  const { checkPermissions } = useAppContext();
  const canCreate = checkPermissions(["CreateNotes"]);

  // Zero notes:
  //   - viewer can create → show muted "compose" ghost as a first-note entry point
  //   - viewer cannot create → render nothing (no actionable target)
  if (!count) {
    if (!interactive || !canCreate) return null;

    const composeLabel = t("notes.indicator_compose_title") as string;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={classNames(
          "inline-flex items-center rounded px-1.5 py-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500",
          className,
        )}
        aria-label={composeLabel}
        title={composeLabel}
      >
        <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    );
  }

  const label = t("notes.indicator_title", { count }) as string;
  const ariaLabel = t("notes.indicator_aria_label", { count }) as string;

  if (!interactive) {
    return (
      <span
        className={classNames(
          "inline-flex items-center gap-1 text-gray-500",
          className,
        )}
        aria-label={ariaLabel}
        title={label}
      >
        <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
        <span className="text-xs font-medium">{count}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={classNames(
        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500",
        className,
      )}
      aria-label={ariaLabel}
      title={label}
    >
      <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
      <span className="text-xs font-medium">{count}</span>
    </button>
  );
};

export default NoteIndicatorBadge;
